import React, {useState, useEffect, useMemo, useCallback} from 'react';
import {useSelector} from 'react-redux';
import Moment from 'react-moment';
import _ from 'lodash';
import styled from 'styled-components';
import {SVGICONS} from '@ey/icons';
import {currentResource, labels} from '../../../util/utils';
import {dateFormats, AssignmentStatuses} from '../../../util/uiconstants';
import Utility from '../../../util/utilityfunctions';
import {getUserPreferences} from '../../../selectors/Ares/selectors';
import TeamsInfo from '../TeamsUser/TeamsInfo';
import ManageUserAssignments from '../ReviewerAssignments/ManageUserAssignments';
import EllipsesControl from '../EllipsesControl/EllipsesControl';
import MotifDropdown from '@ey/motif-react-wrapper/Dropdown';
import MotifButton from '@ey/motif-react-wrapper/Button';

function ReviewerAssignments(props) {
	const {task, engagementId, taskId, taskTypeId, statusId, isAresView} = props;
	const [showDropdown, setShowDropdown] = useState(false);
	const [currentAssignment, setCurrentAssignment] = useState(null);

	const userPreferences = useSelector(getUserPreferences);

	const handleToggle = useCallback(() => {
		setShowDropdown(!showDropdown);
	}, [showDropdown]);

	const setClickOutside = useCallback(() => {
		setShowDropdown(false);
	}, []);

	const getCurrentAssignment = useCallback(() => {
		return task?.assignments?.find((item) => item.isCurrent === true);
	}, [task]);

	const isClosed = useCallback(() => {
		return (
			!_.isEmpty(task?.assignments) &&
			_.isEmpty(task?.assignments?.filter((item) => item.assignmentStatusId !== AssignmentStatuses.Complete))
		);
	}, [task]);

	const getRoleNameForMember = (roleId) => {
		const role = _.find(currentResource.roleForMember, ['id', roleId]);
		return _.get(role, 'role', 'N/A');
	};

	const getAssignmentType = (assignmentTypeId) => {
		return currentResource.reviewerAssignments.AssignmentType.find((at) => at.id === assignmentTypeId).displayName;
	};

	const getTeamsInfo = useMemo(() => {
		return (
			<section className="assignedtoUserIdIcon">
				<TeamsInfo
					personData={{
						person: {
							emailAddress: currentAssignment?.emailAddress,
							firstName: currentAssignment?.firstName,
							lastName: currentAssignment?.lastName
						},
						viewType: 'image',
						customTemplate: 'no-data'
					}}
					personCardInteraction={'none'}
					theme={Utility.getTheme()}
					key={'userAvatar'}
				/>
			</section>
		);
	}, [currentAssignment]);

	useEffect(() => {
		if (task) {
			setCurrentAssignment(getCurrentAssignment());
		}
	}, [task]);

	return (
		<StyledRelatedAssignments className="StyledRelatedAssignments">
			{task && task.assignments && task.assignments.length === 0 ? (
				<React.Fragment>
					<section className="unassignedtoUserIdIcon">
						<SVGICONS name="user-page" styleName="medium" />
					</section>
					<section className="unassignedToWrapper">
						<span className="btn-label ellipsis" title={labels.assignedTo}>
							{labels.assignedTo}
						</span>
						<span className="btnValue ellipsis">{labels.unassigned}</span>
					</section>
				</React.Fragment>
			) : (
				<React.Fragment>
					{!isClosed() &&
					currentAssignment &&
					currentAssignment.assignedToUserId &&
					!Utility.isEmptyGuid(currentAssignment.assignedToUserId) ? (
						<>{getTeamsInfo}</>
					) : (
						<section className="unassignedtoUserIdIcon">
							<SVGICONS name="user-page" styleName="medium" />
						</section>
					)}
					<MotifDropdown
						open={showDropdown}
						portalClassName={`${!isAresView ? 'neon-dropdownportal' : ''} assignedto-dropdownportal`}
						handleOnClose={setClickOutside}
						trigger={
							<MotifButton
								variant="text"
								onClick={handleToggle}
								label={currentResource.reviewerAssignments.taskLayoutHeaderAssignments}
							>
								<section className="btn-inner-wrap">
									<span className="btn-label ellipsis assignedTo" title={labels.assignedTo}>
										<span className="btn-label-ellipsis">
											<EllipsesControl content={labels.assignedTo} tooltip={labels.assignedTo} noOfLines={1} />
										</span>
										<span className="btn-label-ellipsis">
											<EllipsesControl
												content={
													!isClosed() && task?.assignments?.length > 0 && currentAssignment
														? ' ' + getAssignmentType(currentAssignment.assignmentTypeId)
														: ''
												}
												tooltip={
													!isClosed() && task?.assignments?.length > 0 && currentAssignment
														? ' ' + getAssignmentType(currentAssignment.assignmentTypeId)
														: ''
												}
												noOfLines={1}
											/>
										</span>
									</span>
									<span className="btnValueWrp">
										{isClosed() ? (
											<span className="btnValue ellipsis">{labels.closed}</span>
										) : task?.assignments?.length === 0 ? (
											<span className="btnValue ellipsis">{labels.unassigned}</span>
										) : (
											// If no records for current assignment, continue to show single assignmenet that task has
											<>
												{_.isEmpty(currentAssignment) ? (
													<>
														{task && task.assignments && task.assignments[0].userInitials ? (
															<>
																<span
																	title={`${task?.assignments[0].firstName} ${task?.assignments[0].lastName}`}
																	className="user-name btnValue ellipsis"
																>
																	{`${task?.assignments[0].firstName} ${task?.assignments[0].lastName}`}
																</span>
															</>
														) : (
															<>
																{task && task.assignments && task.assignments[0].assignedToRoleId && (
																	<>
																		<span className="user-name btnValue ellipsis">
																			{getRoleNameForMember(task?.assignments[0].assignedToRoleId)}
																		</span>
																	</>
																)}
															</>
														)}
													</>
												) : currentAssignment?.userInitials ? (
													<>
														<span
															title={`${currentAssignment?.firstName} ${currentAssignment?.lastName}`}
															className="user-name btnValue ellipsis"
														>
															{`${currentAssignment?.firstName} ${currentAssignment?.lastName}`}
														</span>
													</>
												) : (
													currentAssignment?.assignedToRoleId && (
														<>
															<span className="user-name btnValue ellipsis">
																{getRoleNameForMember(currentAssignment?.assignedToRoleId)}
															</span>
														</>
													)
												)}
												<span className="assignment-date">
													{task && task.assignments && task.assignments[0].taskTimePhase?.dueDate && (
														<>
															{` (`}
															<Moment
																format={Utility.getDateFormatWithoutYear(
																	dateFormats[userPreferences.dateFormat].momentLocale
																)}
																subtract={{
																	days: _.isEmpty(currentAssignment)
																		? task?.assignments[0].dueDays
																		: currentAssignment?.dueDays || 0
																}}
															>
																{task?.assignments[0].taskTimePhase?.dueDate}
															</Moment>
															{`)`}
														</>
													)}
													{task?.assignments?.filter((item) => item.assignmentStatusId !== AssignmentStatuses.Complete)
														.length > 1 &&
														` + ${task?.assignments?.filter(
															(item) => item.assignmentStatusId !== AssignmentStatuses.Complete
														).length - 1}`}
												</span>
											</>
										)}
									</span>
								</section>
							</MotifButton>
						}
					>
						<ManageUserAssignments
							engagementId={Number(engagementId)}
							taskId={taskId}
							assignments={task?.assignments}
							taskTypeId={taskTypeId}
							taskStatusId={statusId}
							timePhaseId={task?.timePhaseId || task?.timephase?.id}
							setClickOutside={setClickOutside}
						/>
					</MotifDropdown>
				</React.Fragment>
			)}
		</StyledRelatedAssignments>
	);
}

export default ReviewerAssignments;

const StyledRelatedAssignments = styled.section`
	display: flex;
	align-items: center;
	.assignedtoUserIdIcon {
		height: var(--px-32);
		width: var(--px-32);
		cursor: default;
		display: flex;
		align-items: center;
		height: var(--px-45);
		margin-right: var(--px-3);
		.userdata {
			.customPersonCard {
				--avatar-size: var(--px-22) !important;
				.react-loading-skeleton {
					width: var(--px-22) !important;
					height: var(--px-22) !important;
				}
			}
			.userimghold {
				height: var(--px-32);
				width: var(--px-32);
				.userimg {
					height: var(--px-32);
					width: var(--px-32);
					margin-right: 0;
				}
			}
		}
	}
	.unassignedtoUserIdIcon {
		display: flex;
		align-items: center;
		cursor: default;
		height: var(--px-45);
		svg {
			width: var(--px-32) !important;
			max-width: var(--px-32) !important;
			height: var(--px-32) !important;
			max-height: var(--px-32) !important;
			cursor: default;
			fill: var(--neutrals-900);
		}
	}
	.motif-dropdown {
		.motif-button {
			border: 0 !important;
		}
	}
	.unassignedToWrapper {
		display: flex;
		margin-left: var(--px-10);
		flex-direction: column;
		line-height: normal;
		.btn-label,
		.btnValue {
			font-weight: 300;
			font-size: var(--px-12);
			color: var(--neutrals-900);
			line-height: var(--px-18);
		}
		.btnValue {
			font-weight: 400;
			font-size: var(--px-12);
			color: var(--neutrals-900);
			line-height: var(--px-18);
		}
	}
	.assignedto-dropdownportal {
		.btnValueWrp .btnValue {
			font-weight: 400;
		}
	}
	button.motif-button,
	button.motif-icon-button {
		height: var(--px-39);
		display: flex;
		align-items: center;
		.btn-inner-wrap {
			display: flex;
			flex-direction: column;
			align-items: baseline;
			justify-content: center;
			.btn-label {
				display: flex;
				.btn-label-ellipsis {
					max-width: var(--px-120);
					margin-right: var(--px-5);
				}
			}
			.btnValueWrp {
				display: flex;
				width: 100%;
				align-items: center;
				.btnValue {
					max-width: var(--px-160);
					margin-right: var(--px-5);
					overflow: hidden;
					text-overflow: ellipsis;
				}
				.user-name,
				.btnValue {
					overflow: hidden;
					text-overflow: ellipsis;
					padding-top: 0;
				}
			}
			.assignedTo {
				font-size: var(--px-12);
				line-height: var(--px-18);
			}
			.btnValueWrp {
				line-height: var(--px-20);
			}
		}

		.btnValue {
			font-weight: bold;
			line-height: normal;
			padding-top: var(--px-4);
			&:empty {
				display: none;
			}
			span {
				font-weight: normal;
			}
		}
		svg {
			&.large.left {
				margin-right: 0;
				margin-top: calc(var(--px-3) * -1);
				margin-left: calc(var(--px-5) * -1);
			}
			&.arrow-icon {
				margin-right: var(--px-4);
				margin-left: var(--px-5);
				transform: rotate(-180deg);
				margin-top: calc(var(--px-2) * -1);
			}
		}
		.initials-badge {
			margin-right: var(--px-10);
			width: var(--px-22);
			height: var(--px-22);
			border-radius: var(--px-50);
			line-height: var(--px-22);
			font-size: var(--px-10);
			color: var(--neutrals-00white);
			background-color: var(--blue-500);
			text-align: center;
		}
		&:hover,
		&:focus {
			svg {
				background: none;
				border-radius: 0;
			}
		}
	}
	.motif-dropdown {
		button {
			padding-right: var(--px-4) !important;
			padding-left: var(--px-4) !important;
			border-radius: var(--px-8) !important;
			&:focus {
				border: var(--px-1) solid var(--blue-600);
			}
		}
	}
`;
